import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer, Box, Typography, List, ListItem, ListItemText, IconButton, Button, Divider,
  Chip, CircularProgress, Alert, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
  Close as CloseIcon, Restore as RestoreIcon, CloudDone as DeployedIcon, History as HistoryIcon,
} from '@mui/icons-material';
import { useFlow } from '../../contexts/FlowContext';

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

/**
 * Right-hand drawer showing a flow's saved version history (manual saves + deployment snapshots),
 * newest first, with the ability to restore any version. Restore is reversible, the current state
 * is snapshotted first server-side.
 */
const VersionHistoryDrawer = ({ open, onClose, flowId, onRestored }) => {
  const { loadVersions, restoreVersion } = useFlow();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [restoringNumber, setRestoringNumber] = useState(null);
  // Version pending a restore confirmation (null = no dialog open). Restore is destructive to the
  // editor's current state, so we confirm first, even though it's reversible server-side.
  const [confirmVersion, setConfirmVersion] = useState(null);

  const refresh = useCallback(async () => {
    if (!flowId) return;
    setLoading(true);
    setError(null);
    try {
      setVersions(await loadVersions(flowId));
    } catch (err) {
      setError(err.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [flowId, loadVersions]);

  useEffect(() => {
    if (open) {
      refresh();
    }
  }, [open, refresh]);

  const handleRestore = async (versionNumber) => {
    setConfirmVersion(null);
    setRestoringNumber(versionNumber);
    setError(null);
    try {
      const restored = await restoreVersion(flowId, versionNumber);
      if (onRestored) onRestored(restored, versionNumber);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to restore version');
    } finally {
      setRestoringNumber(null);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 380, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 2 }}>
          <HistoryIcon color="primary" />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>Version history</Typography>
          <IconButton onClick={onClose} aria-label="Close version history"><CloseIcon /></IconButton>
        </Box>
        <Divider />

        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : versions.length === 0 ? (
          <Box sx={{ textAlign: 'center', color: 'text.secondary', px: 3, py: 6 }}>
            <Typography variant="body2">No saved versions yet.</Typography>
            <Typography variant="caption">A version is recorded each time you save or deploy this flow.</Typography>
          </Box>
        ) : (
          <List sx={{ overflowY: 'auto', flexGrow: 1, py: 0 }}>
            {versions.map((v) => (
              <React.Fragment key={v.id}>
                <ListItem
                  alignItems="flex-start"
                  secondaryAction={
                    <Tooltip title="Restore this version">
                      <span>
                        <Button
                          size="small"
                          startIcon={restoringNumber === v.versionNumber ? <CircularProgress size={14} /> : <RestoreIcon />}
                          disabled={restoringNumber != null}
                          onClick={() => setConfirmVersion(v)}
                        >
                          Restore
                        </Button>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          v{v.versionNumber}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                          {v.changeDescription || 'Saved'}
                        </Typography>
                        {v.deployedAt && (
                          <Chip size="small" color="success" variant="outlined" icon={<DeployedIcon />} label="Deployed" />
                        )}
                      </Box>
                    }
                    secondary={formatWhen(v.createdAt)}
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      <Dialog open={confirmVersion != null} onClose={() => setConfirmVersion(null)}>
        <DialogTitle>Restore version {confirmVersion ? `v${confirmVersion.versionNumber}` : ''}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This replaces what&apos;s currently in the editor with
            {confirmVersion ? ` v${confirmVersion.versionNumber}` : ' this version'}. Your current
            state is snapshotted first, so you can restore back to it at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmVersion(null)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<RestoreIcon />}
            onClick={() => handleRestore(confirmVersion.versionNumber)}
          >
            Confirm restore
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default VersionHistoryDrawer;
